import { ReviewField } from 'api';

/** The API's field path grammar: in `sites[0].cis`, `sites` is the repeating
 * group, 0 the instance and `cis` the leaf. Display only -- field names go back
 * to the API unchanged. */

const INDEX_PATTERN = /\[(\d+)\]/;

/** `sites[0].cis` -> `sites`. Null for a document-level field. */
export const groupPrefix = (fieldName: string): string | null => {
  const bracket = fieldName.indexOf('[');
  return bracket > 0 ? fieldName.slice(0, bracket) : null;
};

/** `sites[0].cis` -> 0. Null for a document-level field. */
export const instanceIndex = (fieldName: string): number | null => {
  const match = INDEX_PATTERN.exec(fieldName);
  return match ? Number(match[1]) : null;
};

/** `sites[0].cis` -> `cis`, for labels inside a group's panel. */
export const leafKey = (fieldName: string): string => {
  const marker = fieldName.indexOf('].');
  return marker >= 0 ? fieldName.slice(marker + 2) : fieldName;
};

export type FieldGroup = {
  /** `sites[0]`, the panel's identity. */
  key: string;
  groupPrefix: string;
  index: number;
  fields: ReviewField[];
};

export type GroupedFields = {
  documentFields: ReviewField[];
  groups: FieldGroup[];
};

/** Splits the fields into document-level fields and one group per instance.
 * Keeps the API's order, which already lists each instance's fields together. */
export const groupFields = (fields: ReviewField[]): GroupedFields => {
  const documentFields: ReviewField[] = [];
  const groups: FieldGroup[] = [];
  const byKey = new Map<string, FieldGroup>();

  fields.forEach(field => {
    const prefix = groupPrefix(field.fieldName);
    const index = instanceIndex(field.fieldName);

    if (prefix == null || index == null) {
      documentFields.push(field);
      return;
    }

    const key = `${prefix}[${index}]`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, groupPrefix: prefix, index, fields: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.fields.push(field);
  });

  return { documentFields, groups };
};

/** True when every leaf is null: the API's placeholder for a repeating group
 * the model returned empty (`sites: []`). */
export const isEmptyGroup = (group: FieldGroup): boolean =>
  group.fields.every(f => f.value == null);

/** Singular, capitalized header: `sites` -> `Site`. */
export const groupLabel = (prefix: string): string => {
  const singular = prefix.endsWith('s') ? prefix.slice(0, -1) : prefix;
  return singular.charAt(0).toUpperCase() + singular.slice(1);
};
